<template>
  <div class="informe-uso-container">
    <CardPrime class="informe-uso-card">
      <template #title>
        <div class="card-header">
          <h3 class="card-title">Informe Total Múltiple - Pruebas, Prácticas, Videos y Juegos</h3>
        </div>
      </template>
      <template #content>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div class="form-field">
            <label for="colegio-select" class="block text-sm font-medium text-gray-700">Seleccionar Colegio:</label>
            <ColegioDropdown
              v-model="selectedColegio"
              :mostrarErrores="mostrarErrores"
              :tooltip="'Selecciona un Colegio'"
              :error="errors.colegio"
              :options="colegios"
              optionLabel="nombre"
              optionValue="id_colegio"
              placeholder="Selecciona un colegio"
              @update:modelValue="cargarCursosYAsignaturas"
            />
            <small v-if="mostrarErrores && errors.colegio" class="p-error">{{ errors.colegio }}</small>
          </div>
          <div class="form-field">
            <label for="cursos-select" class="block text-sm font-medium text-gray-700">Seleccionar Cursos:</label>
            <CursoDropdown
              v-model="selectedCursos"
              :mostrarErrores="mostrarErrores"
              :tooltip="'Selecciona uno o más cursos'"
              :error="errors.cursos"
              :options="cursos"
              :multiple="true"
              optionLabel="nombre"
              optionValue="id_curso"
              placeholder="Selecciona uno o más cursos"
              :disabled="!selectedColegio"
              @update:modelValue="onCursoChange"
            />
            <small v-if="mostrarErrores && errors.cursos" class="p-error">{{ errors.cursos }}</small>
          </div>
          <div class="form-field">
            <label for="asignaturas-select" class="block text-sm font-medium text-gray-700">Seleccionar Asignaturas:</label>
            <AsignaturaDropdown
              v-model="selectedAsignaturas"
              :mostrarErrores="mostrarErrores"
              :tooltip="'Selecciona una o más asignaturas'"
              :error="errors.asignaturas"
              :options="asignaturas"
              :multiple="true"
              optionLabel="glosa"
              optionValue="id_asignatura"
              placeholder="Selecciona una o más asignaturas"
              :disabled="!selectedColegio"
              @update:modelValue="onAsignaturaChange"
            />
            <small v-if="mostrarErrores && errors.asignaturas" class="p-error">{{ errors.asignaturas }}</small>
          </div>
          <div class="form-field">
            <label for="inicio" class="block text-sm font-medium text-gray-700">Fecha Inicio:</label>
            <input
              type="date"
              id="inicio"
              v-model="inicio"
              class="p-inputtext w-full border-gray-300 rounded-md shadow-sm"
            />
          </div>
          <div class="form-field">
            <label for="fin" class="block text-sm font-medium text-gray-700">Fecha Fin:</label>
            <input
              type="date"
              id="fin"
              v-model="fin"
              class="p-inputtext w-full border-gray-300 rounded-md shadow-sm"
            />
          </div>
        </div>
        <ButtonPrime
          label="Generar Informe"
          @click="generarInformeTotal"
          class="mb-4 mr-2"
          :disabled="loading"
        />
        <ButtonPrime
          label="Exportar a Excel"
          icon="pi pi-file-excel"
          class="p-button-success mb-4 mr-2"
          @click="exportToExcel"
          :disabled="loading || (!resumenPruebas.length && !resumenPracticas.length && !resumenVideos.length && !resumenJuegos.length)"
        />
        <!-- Commented out the Export to Word button -->
        <!--
        <ButtonPrime
          label="Exportar a Word"
          icon="pi pi-file"
          class="p-button-info mb-4"
          @click="exportToWord"
          :disabled="loading || (!resumenPruebas.length && !resumenPracticas.length && !resumenVideos.length && !resumenJuegos.length)"
        />
        -->
        <div v-if="resumenPruebas.length > 0 || resumenPracticas.length > 0 || resumenVideos.length > 0 || resumenJuegos.length > 0" class="mt-4">
          <!-- Tabla de Pruebas -->
          <div v-if="resumenPruebas.length > 0" class="mt-4">
            <h4 class="text-lg font-semibold mb-2">Resumen de Pruebas por Asignatura:</h4>
            <DataTable :value="resumenPruebas" class="p-datatable-sm" dataKey="id_asignatura">
              <ColumnPrime field="nombre_asignatura" header="Asignatura" sortable />
              <ColumnPrime field="correctas" header="Correctas" sortable />
              <ColumnPrime field="incorrectas" header="Incorrectas" sortable />
              <ColumnPrime field="total_preguntas" header="Total Preguntas" sortable />
            </DataTable>
          </div>
          <!-- Tabla de Prácticas -->
          <div v-if="resumenPracticas.length > 0" class="mt-4">
            <h4 class="text-lg font-semibold mb-2">Resumen de Prácticas por Asignatura:</h4>
            <DataTable :value="resumenPracticas" class="p-datatable-sm" dataKey="id_asignatura">
              <ColumnPrime field="nombre_asignatura" header="Asignatura" sortable />
              <ColumnPrime field="correctas" header="Correctas" sortable />
              <ColumnPrime field="incorrectas" header="Incorrectas" sortable />
              <ColumnPrime field="total_preguntas" header="Total Preguntas" sortable />
            </DataTable>
          </div>
          <!-- Tabla de Videos -->
          <div v-if="resumenVideos.length > 0" class="mt-4">
            <h4 class="text-lg font-semibold mb-2">Resumen de Visualizaciones de Videos por Asignatura:</h4>
            <DataTable :value="resumenVideos" class="p-datatable-sm" dataKey="id_asignatura">
              <ColumnPrime field="nombre_asignatura" header="Asignatura" sortable />
              <ColumnPrime field="total_visualizaciones" header="Total Visualizaciones" sortable />
              <ColumnPrime field="correctas" header="Correctas" sortable />
              <ColumnPrime field="incorrectas" header="Incorrectas" sortable />
            </DataTable>
          </div>
          <!-- Tabla de Juegos -->
          <div v-if="resumenJuegos.length > 0" class="mt-4">
            <h4 class="text-lg font-semibold mb-2">Resumen de Juegos por Asignatura:</h4>
            <DataTable :value="resumenJuegos" class="p-datatable-sm" dataKey="id_asignatura">
              <ColumnPrime field="nombre_asignatura" header="Asignatura" sortable />
              <ColumnPrime field="correctas" header="Correctas" sortable />
              <ColumnPrime field="incorrectas" header="Incorrectas" sortable />
              <ColumnPrime field="total_preguntas" header="Total Preguntas" sortable />
            </DataTable>
          </div>
        </div>
        <div
          v-else-if="!loading && wasGenerated"
          class="mt-4 p-4 bg-yellow-100 text-yellow-700 rounded"
        >
          No se encontraron datos para los filtros seleccionados.
        </div>
        <ToastPrime />
      </template>
    </CardPrime>
  </div>
</template>

<script>
import usuarioService from '@/services/UsuarioService';
import CardPrime from 'primevue/card';
import ButtonPrime from 'primevue/button';
import DataTable from 'primevue/datatable';
import ColumnPrime from 'primevue/column';
import ToastPrime from 'primevue/toast';
import CursoDropdown from '@/components/selectores/CursoDropdown.vue';
import AsignaturaDropdown from '@/components/selectores/AsignaturaDropdown.vue';
import ColegioDropdown from '@/components/selectores/ColegioDropdown.vue';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default {
  components: {
    CardPrime,
    ButtonPrime,
    DataTable,
    ColumnPrime,
    ToastPrime,
    CursoDropdown,
    AsignaturaDropdown,
    ColegioDropdown,
  },
  data() {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      selectedColegio: '',
      selectedCursos: [],
      selectedAsignaturas: [],
      colegios: [],
      cursos: [],
      asignaturas: [],
      inicio: firstDay.toISOString().split('T')[0],
      fin: lastDay.toISOString().split('T')[0],
      resumenPruebas: [],
      resumenPracticas: [],
      resumenVideos: [],
      resumenJuegos: [],
      loading: false,
      wasGenerated: false,
      errors: {
        colegio: '',
        cursos: '',
        asignaturas: '',
      },
      mostrarErrores: false,
    };
  },
  methods: {
    async loadColegios() {
      try {
        this.loading = true;
        const response = await usuarioService.getColegios();
        this.colegios = response.data || [];
        if (this.colegios.length === 0) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Advertencia',
            detail: 'No se encontraron colegios.',
            life: 3000,
          });
        }
      } catch (error) {
        console.error('Error al cargar colegios:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'No se pudieron cargar los colegios.',
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    async cargarCursosYAsignaturas() {
      this.selectedCursos = [];
      this.selectedAsignaturas = [];
      this.cursos = [];
      this.asignaturas = [];
      if (!this.selectedColegio) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Advertencia',
          detail: 'Por favor, selecciona un colegio.',
          life: 3000,
        });
        return;
      }
      this.loading = true;
      try {
        const cursosResponse = await usuarioService.getCursosPorColegio(this.selectedColegio);
        this.cursos = cursosResponse.data || [];
        if (this.cursos.length === 0) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Advertencia',
            detail: 'No se encontraron cursos para el colegio seleccionado.',
            life: 3000,
          });
        }
        const asignaturasResponse = await usuarioService.getAsignaturasPorColegio(this.selectedColegio);
        this.asignaturas = asignaturasResponse.data || [];
        if (this.asignaturas.length === 0) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Advertencia',
            detail: 'No se encontraron asignaturas para el colegio seleccionado.',
            life: 3000,
          });
        }
      } catch (error) {
        console.error('Error al cargar cursos o asignaturas:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'No se pudieron cargar los cursos o asignaturas.',
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    async generarInformeTotal() {
      this.mostrarErrores = false;
      this.wasGenerated = true;
      if (!this.selectedColegio) {
        this.errors.colegio = 'Debes seleccionar un colegio';
        this.mostrarErrores = true;
      }
      if (this.selectedCursos.length === 0) {
        this.errors.cursos = 'Debes seleccionar al menos un curso';
        this.mostrarErrores = true;
      }
      if (!this.inicio || !this.fin || new Date(this.inicio) > new Date(this.fin)) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Advertencia',
          detail: 'Fechas inválidas o incompletas.',
          life: 3000,
        });
        return;
      }
      if (this.mostrarErrores) return;
      this.loading = true;
      try {
        const response = await usuarioService.getInformeTotalMultiple(
          this.inicio,
          this.fin,
          this.selectedColegio,
          this.selectedCursos,
          this.selectedAsignaturas.length > 0 ? this.selectedAsignaturas : null
        );
        console.log('Respuesta del servidor:', JSON.stringify(response, null, 2));
        if (
          !response ||
          !response.ResumenPruebas ||
          !Array.isArray(response.ResumenPruebas) ||
          !response.ResumenPracticas ||
          !Array.isArray(response.ResumenPracticas) ||
          !response.ResumenVideos ||
          !Array.isArray(response.ResumenVideos) ||
          !response.ResumenJuegos ||
          !Array.isArray(response.ResumenJuegos)
        ) {
          throw new Error('Respuesta inválida del servidor: datos no disponibles.');
        }
        this.resumenPruebas = response.ResumenPruebas;
        this.resumenPracticas = response.ResumenPracticas;
        this.resumenVideos = response.ResumenVideos;
        this.resumenJuegos = response.ResumenJuegos;
        console.log('Datos asignados:', {
          resumenPruebas: JSON.stringify(this.resumenPruebas, null, 2),
          resumenPracticas: JSON.stringify(this.resumenPracticas, null, 2),
          resumenVideos: JSON.stringify(this.resumenVideos, null, 2),
          resumenJuegos: JSON.stringify(this.resumenJuegos, null, 2),
        });
        this.$toast.add({
          severity: 'success',
          summary: 'Éxito',
          detail: response.Mensaje || 'Informe total generado con éxito.',
          life: 3000,
        });
      } catch (error) {
        console.error('Error al generar informe total:', error);
        this.resumenPruebas = [];
        this.resumenPracticas = [];
        this.resumenVideos = [];
        this.resumenJuegos = [];
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'No se pudo generar el informe.',
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    exportToExcel() {
      console.log('Iniciando exportación a Excel...');
      if (!this.resumenPruebas.length && !this.resumenPracticas.length && !this.resumenVideos.length && !this.resumenJuegos.length) {
        console.warn('No hay datos para exportar a Excel');
        this.$toast.add({
          severity: 'warn',
          summary: 'Advertencia',
          detail: 'No hay datos para exportar.',
          life: 3000,
        });
        return;
      }

      try {
        console.log('Datos para Excel:', {
          pruebas: JSON.stringify(this.resumenPruebas, null, 2),
          practicas: JSON.stringify(this.resumenPracticas, null, 2),
          videos: JSON.stringify(this.resumenVideos, null, 2),
          juegos: JSON.stringify(this.resumenJuegos, null, 2),
        });

        const colegio = this.colegios.find((c) => c.id_colegio === this.selectedColegio)?.nombre || 'No especificado';
        const cursos = this.selectedCursos
          .map((id) => this.cursos.find((c) => c.id_curso === id)?.nombre || id)
          .join(', ');
        const asignaturas = this.selectedAsignaturas.length > 0
          ? this.selectedAsignaturas
              .map((id) => this.asignaturas.find((a) => a.id_asignatura === id)?.glosa || id)
              .join(', ')
          : 'Todas';

        console.log('Metadatos:', { colegio, cursos, asignaturas, inicio: this.inicio, fin: this.fin });

        // Agrupar y sumar los datos de juegos por nombre_asignatura
        const groupedJuegos = this.resumenJuegos.reduce((acc, item) => {
          const asignatura = item.nombre_asignatura || 'N/A';
          if (!acc[asignatura]) {
            acc[asignatura] = {
              nombre_asignatura: asignatura,
              correctas: 0,
              incorrectas: 0,
              total_preguntas: 0,
            };
          }
          acc[asignatura].correctas += item.correctas ?? 0;
          acc[asignatura].incorrectas += item.incorrectas ?? 0;
          acc[asignatura].total_preguntas += item.total_preguntas ?? 0;
          return acc;
        }, {});

        const juegosData = Object.values(groupedJuegos);
        console.log('Datos de juegos agrupados:', JSON.stringify(juegosData, null, 2));

        const data = [
          ['Informe Total Múltiples - Pruebas, Prácticas, Videos y Juegos'],
          [''],
          ['Colegio:', colegio],
          ['Cursos:', cursos],
          ['Asignaturas:', asignaturas],
          ['Fecha Inicio:', this.inicio],
          ['Fecha Fin:', this.fin],
          [''],
          ['Resumen de Pruebas por Asignatura'],
          ['Asignatura', 'Correctas', 'Incorrectas', 'Total Preguntas'],
          ...this.resumenPruebas.map((item, index) => {
            console.log(`Fila de pruebas ${index}: asignatura=${item.nombre_asignatura || 'N/A'} correctas=${item.correctas ?? 0} incorrectas=${item.incorrectas ?? 0} total_preguntas=${item.total_preguntas ?? 0}`);
            return [
              item.nombre_asignatura || 'N/A',
              item.correctas ?? 0,
              item.incorrectas ?? 0,
              item.total_preguntas ?? 0,
            ];
          }),
          [''],
          ['Resumen de Prácticas por Asignatura'],
          ['Asignatura', 'Correctas', 'Incorrectas', 'Total Preguntas'],
          ...this.resumenPracticas.map((item, index) => {
            console.log(`Fila de prácticas ${index}: asignatura=${item.nombre_asignatura || 'N/A'} correctas=${item.correctas ?? 0} incorrectas=${item.incorrectas ?? 0} total_preguntas=${item.total_preguntas ?? 0}`);
            return [
              item.nombre_asignatura || 'N/A',
              item.correctas ?? 0,
              item.incorrectas ?? 0,
              item.total_preguntas ?? 0,
            ];
          }),
          [''],
          ['Resumen de Visualizaciones de Videos por Asignatura'],
          ['Asignatura', 'Total Visualizaciones', 'Correctas', 'Incorrectas'],
          ...this.resumenVideos.map((item, index) => {
            console.log(`Fila de videos ${index}: asignatura=${item.nombre_asignatura || 'N/A'} total_visualizaciones=${item.total_visualizaciones ?? 0} correctas=${item.correctas ?? 0} incorrectas=${item.incorrectas ?? 0}`);
            return [
              item.nombre_asignatura || 'N/A',
              item.total_visualizaciones ?? 0,
              item.correctas ?? 0,
              item.incorrectas ?? 0,
            ];
          }),
          [''],
          ['Resumen de Juegos por Asignatura'],
          ['Asignatura', 'Correctas', 'Incorrectas', 'Total Preguntas'],
          ...juegosData.map((item, index) => {
            console.log(`Fila de juegos ${index}: asignatura=${item.nombre_asignatura} correctas=${item.correctas} incorrectas=${item.incorrectas} total_preguntas=${item.total_preguntas}`);
            return [
              item.nombre_asignatura,
              item.correctas,
              item.incorrectas,
              item.total_preguntas,
            ];
          }),
        ];

        console.log('Datos para la hoja de Excel generados, longitud:', data.length);

        const worksheet = XLSX.utils.aoa_to_sheet(data);
        console.log('Hoja de Excel creada');

        worksheet['!cols'] = [
          { wch: 30 }, // Asignatura
          { wch: 15 }, // Correctas / Total Visualizaciones
          { wch: 15 }, // Incorrectas
          { wch: 15 }, // Total Preguntas
        ];

        worksheet['!rows'] = [
          { hpt: 30 }, // Title row
          {}, // Empty row
          {}, // Colegio
          {}, // Cursos
          {}, // Asignaturas
          {}, // Fecha Inicio
          {}, // Fecha Fin
          {}, // Empty row
          { hpt: 20 }, // Header row (Pruebas)
          ...this.resumenPruebas.map(() => ({})),
          {}, // Empty row
          { hpt: 20 }, // Header row (Prácticas)
          ...this.resumenPracticas.map(() => ({})),
          {}, // Empty row
          { hpt: 20 }, // Header row (Videos)
          ...this.resumenVideos.map(() => ({})),
          {}, // Empty row
          { hpt: 20 }, // Header row (Juegos)
          ...juegosData.map(() => ({})),
        ];

        worksheet['A1'] = {
          v: 'Informe Total Múltiples - Pruebas, Prácticas, Videos y Juegos',
          s: {
            font: { bold: true, sz: 16 },
            alignment: { horizontal: 'center', vertical: 'center' },
          },
        };

        const headers = [
          ['A9', 'B9', 'C9', 'D9'],
          [`A${10 + this.resumenPruebas.length + 1}`, `B${10 + this.resumenPruebas.length + 1}`, `C${10 + this.resumenPruebas.length + 1}`, `D${10 + this.resumenPruebas.length + 1}`],
          [`A${10 + this.resumenPruebas.length + this.resumenPracticas.length + 2}`, `B${10 + this.resumenPruebas.length + this.resumenPracticas.length + 2}`, `C${10 + this.resumenPruebas.length + this.resumenPracticas.length + 2}`, `D${10 + this.resumenPruebas.length + this.resumenPracticas.length + 2}`],
          [`A${10 + this.resumenPruebas.length + this.resumenPracticas.length + this.resumenVideos.length + 3}`, `B${10 + this.resumenPruebas.length + this.resumenPracticas.length + this.resumenVideos.length + 3}`, `C${10 + this.resumenPruebas.length + this.resumenPracticas.length + this.resumenVideos.length + 3}`, `D${10 + this.resumenPruebas.length + this.resumenPracticas.length + this.resumenVideos.length + 3}`],
        ];

        headers.forEach(headerRow => {
          headerRow.forEach(cell => {
            worksheet[cell] = {
              ...worksheet[cell],
              s: {
                font: { bold: true },
                fill: { fgColor: { rgb: 'D3D3D3' } },
                border: {
                  top: { style: 'thin', color: { rgb: '000000' } },
                  bottom: { style: 'thin', color: { rgb: '000000' } },
                  left: { style: 'thin', color: { rgb: '000000' } },
                  right: { style: 'thin', color: { rgb: '000000' } },
                },
                alignment: { horizontal: 'center', vertical: 'center' },
              },
            };
          });
        });

        ['A3', 'A4', 'A5', 'A6', 'A7'].forEach((cell, index) => {
          worksheet[cell] = {
            ...worksheet[cell],
            s: {
              font: { bold: true },
              alignment: { horizontal: 'left', vertical: 'center' },
            },
          };
          const valueCell = `B${3 + index}`;
          worksheet[valueCell] = {
            v: data[2 + index][1],
            s: {
              alignment: { horizontal: 'left', vertical: 'center' },
            },
          };
        });

        console.log('Estilos aplicados a la hoja de Excel');

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'InformeTotalMultiple');
        console.log('Libro de Excel creado');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        console.log('Buffer de Excel generado, tamaño:', excelBuffer.length);

        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        console.log('Blob de Excel creado, tamaño:', blob.size);

        saveAs(blob, `Informe_Total_Multiple_${new Date().toISOString().split('T')[0]}.xlsx`);
        console.log('Archivo Excel descargado');

        this.$toast.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Informe total exportado a Excel correctamente.',
          life: 3000,
        });
      } catch (error) {
        console.error('Error al exportar a Excel:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo exportar el informe a Excel: ' + error.message,
          life: 3000,
        });
      }
    },
    onCursoChange(newValue) {
      this.selectedCursos = newValue || [];
      this.errors.cursos = '';
    },
    onAsignaturaChange(newValue) {
      this.selectedAsignaturas = newValue || [];
      this.errors.asignaturas = '';
    },
  },
  mounted() {
    this.loadColegios();
  },
};
</script>

<style scoped>
.informe-uso-container {
  @apply p-4;
}
.informe-uso-card {
  @apply bg-white rounded-lg shadow p-4;
}
.card-header {
  @apply flex justify-between items-center mb-4;
}
.card-title {
  @apply text-xl font-bold text-gray-800;
}
.form-field {
  @apply mb-4;
}
.form-field label {
  @apply block text-sm font-medium text-gray-700 mb-1;
}
.p-inputtext {
  @apply border-gray-300 rounded-md shadow-sm;
}
:deep(.p-datatable-sm) .p-datatable-thead > tr > th,
:deep(.p-datatable-sm) .p-datatable-tbody > tr > td {
  @apply p-2 text-sm;
}
.flex.justify-end {
  display: flex;
  justify-content: flex-end;
}
</style>